export const CATEGORIES = [
  {
    name: 'Verification & Account Handling',
    attributes: [
      { id: 'V1', label: 'Validated order/invoice and proof of purchase; confirmed no prior refund applied', weight: 4 },
      { id: 'V2', label: 'Confirmed the correct, most up-to-date shipping address (when applicable)', weight: 3 },
      { id: 'V3', label: 'Validated warranty / extended-warranty eligibility (when applicable)', weight: 3 },
    ]
  },
  {
    name: 'Accuracy & Knowledge',
    attributes: [
      { id: 'K1', label: 'Information provided aligns with company policy and guidelines', weight: 8 },
      { id: 'K2', label: 'Technically correct: sound diagnosis / troubleshooting for the issue', weight: 9 },
      { id: 'K3', label: 'Set correct expectations (timeline, process, what happens next)', weight: 4 },
      { id: 'K4', label: 'Used the right tools and resources to handle the case correctly', weight: 4 },
    ]
  },
  {
    name: 'Resolution & Effort',
    attributes: [
      { id: 'R1', label: 'Resolved the reason for contact, or advanced it correctly toward resolution', weight: 7 },
      { id: 'R2', label: 'Minimized customer effort; avoided unnecessary troubleshooting loops', weight: 5 },
      { id: 'R3', label: 'Responded to all of the customer\'s questions', weight: 4 },
      { id: 'R4', label: 'Proposed alternate solutions when appropriate', weight: 4 },
    ]
  },
  {
    name: 'Empathy & Tone',
    attributes: [
      { id: 'E1', label: 'Acknowledged the customer\'s specific situation before troubleshooting', weight: 6 },
      { id: 'E2', label: 'Showed genuine empathy without over-apologizing', weight: 5 },
      { id: 'E3', label: 'Took ownership; warm, human, non-robotic tone', weight: 5 },
      { id: 'E4', label: 'De-escalated / handled a red-flag case with extra care (N/A if none arose)', weight: 4 },
    ]
  },
  {
    name: 'Communication & Clarity',
    attributes: [
      { id: 'C1', label: 'Clear and easy to understand; no jargon dumps', weight: 4 },
      { id: 'C2', label: 'Good use of reading skills; didn\'t make the customer repeat themselves', weight: 3 },
      { id: 'C3', label: 'Provided reference/ticket number and all necessary disclosures', weight: 3 },
    ]
  },
  {
    name: 'Responsiveness & Documentation',
    attributes: [
      { id: 'D1', label: 'Provided follow-ups / responses within SLA', weight: 7 },
      { id: 'D2', label: 'Documented all necessary info: notes, links, next steps, people involved', weight: 8 },
    ]
  }
];

export const TOTAL_ATTRIBUTES = CATEGORIES.reduce((sum, c) => sum + c.attributes.length, 0);

export const PROCESS_DEDUCTIONS = [
  { id: 'PD1', label: 'Missing required tag (e.g., Sabrent - TS / Sabrent - CS)', value: 0.05 },
  { id: 'PD2', label: 'Did not follow the documented process / ticket status', value: 0.05 },
  { id: 'PD3', label: 'Missing critical documentation or notes', value: 0.05 },
];

export const AUTOFAILS = [
  { id: 'AF1', label: 'Rude, dismissive, or argumentative' },
  { id: 'AF2', label: 'Profanity, ridicule, or sarcasm' },
  { id: 'AF3', label: 'Inappropriate comments' },
  { id: 'AF4', label: 'Provided wrong or misleading information' },
  { id: 'AF5', label: 'Authorized a refund/replacement without required validation' },
];

export const RATING_OPTIONS = ['Meets', 'Partial', 'Misses', 'N/A'];

export const RATING_BANDS = [
  { label: 'Excellent', min: 0.95, color: 'bg-green-500' },
  { label: 'Good', min: 0.85, color: 'bg-blue-500' },
  { label: 'Okay', min: 0.70, color: 'bg-yellow-500' },
  { label: 'Needs improvement', min: 0.50, color: 'bg-orange-500' },
  { label: 'Failed', min: 0, color: 'bg-red-500' },
];

export function getRatingBand(score, hasAutofail) {
  if (hasAutofail) return { label: 'Failed', color: 'bg-red-500' };
  for (const band of RATING_BANDS) {
    if (score >= band.min) return band;
  }
  return { label: 'Failed', color: 'bg-red-500' };
}

// Works with partial scores — only counts what's been rated so far
export function calculateScore(scores, deductions, autofails) {
  let earned = 0;
  let applicable = 0;
  let scored = 0;

  for (const cat of CATEGORIES) {
    for (const attr of cat.attributes) {
      const rating = scores[attr.id];
      if (!rating) continue; // not yet scored
      scored++;
      if (rating === 'N/A') continue;
      applicable += attr.weight;
      if (rating === 'Meets') earned += attr.weight;
      else if (rating === 'Partial') earned += attr.weight * 0.5;
    }
  }

  const baseScore = applicable > 0 ? earned / applicable : 0;
  const deductionTotal = deductions.reduce((sum, id) => {
    const d = PROCESS_DEDUCTIONS.find(p => p.id === id);
    return sum + (d ? d.value : 0);
  }, 0);

  const hasAutofail = autofails.length > 0;
  const finalScore = hasAutofail ? 0 : Math.max(0, baseScore - deductionTotal);
  const band = getRatingBand(finalScore, hasAutofail);

  return { baseScore, deductionTotal, finalScore, ratingBand: band.label, ratingColor: band.color, hasAutofail, scored };
}
